// Mental rehearsal pre-fase (Forslag F).
//
// Mellem TrinIntroCard og PreRoundCard. Bruger ser tastatur med
// finger-dots animeret én tast ad gangen ~hvert 2. sek. Brugeren
// SKAL IKKE skrive — kun forestille sig fingerbevægelsen ("motor
// imagery"). Forskning i sport og musik viser at det er målbart
// effektivt for motorisk indlæring.
//
// Aktiveres kun for trin med newKeys.length > 0 og når
// preferences.mentalRehearsalEnabled !== false. Skip-knap er altid
// synlig — vi presser ingen.

import { useEffect, useMemo, useState } from 'react';
import type { KeyboardLayoutId, Trin } from '../types';
import { trinLabel } from '../lib/curriculum';
import { useEnterAction } from '../lib/useEnterAction';
import Keyboard from './Keyboard';

interface MentalRehearsalCardProps {
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  onContinue: () => void;
  onSkip: () => void;
}

const STEP_MS = 1800;
const TOTAL_MS = 15_000;

export default function MentalRehearsalCard({
  trin,
  keyboardLayout,
  onContinue,
  onSkip,
}: MentalRehearsalCardProps) {
  const keys = useMemo(
    () => trin.newKeys.map((k) => (k === ' ' ? ' ' : k.toLowerCase())),
    [trin.newKeys],
  );

  const [step, setStep] = useState(0);
  const [remainingMs, setRemainingMs] = useState(TOTAL_MS);

  useEffect(() => {
    if (keys.length === 0) {
      onContinue();
      return;
    }
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, TOTAL_MS - elapsed);
      setRemainingMs(remaining);
      setStep(Math.floor(elapsed / STEP_MS) % keys.length);
      if (remaining <= 0) {
        window.clearInterval(interval);
      }
    }, 150);
    return () => window.clearInterval(interval);
  }, [keys.length, onContinue]);

  useEnterAction(onContinue);

  const currentKey = keys[step];
  const remainingSec = Math.ceil(remainingMs / 1000);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 animate-fade-in-up">
      <header>
        <p className="label">{trinLabel(trin)} · Mental forberedelse</p>
        <h2 className="mt-1 text-xl font-semibold text-strong">
          Se hvor fingrene skal hen
        </h2>
        <p className="mt-2 text-sm text-muted">
          Sæt fingrene på hjemmerækken og forestil dig bevægelsen mod hver tast.
          Du skal ikke skrive — bare se og mærke. Det aktiverer den motoriske
          del af hjernen, så selve runden bliver lettere.
        </p>
      </header>

      <div className="overflow-hidden rounded-2xl ring-1 ring-zinc-200 dark:ring-white/5">
        <Keyboard
          layoutId={keyboardLayout}
          unlockedKeys={trin.unlockedKeys}
          highlightedKeys={currentKey ? [currentKey] : keys}
          hintLevel="strong"
          showFingerDot
        />
      </div>

      <div className="surface-soft flex items-center justify-between gap-3 px-4 py-3">
        <p className="text-sm text-muted">
          {currentKey && currentKey !== ' '
            ? `Forestil dig fingeren på `
            : 'Forestil dig fingerbevægelsen — '}
          {currentKey && currentKey !== ' ' && (
            <kbd className="font-mono text-base font-semibold text-strong">
              {currentKey.toUpperCase()}
            </kbd>
          )}
        </p>
        <p className="text-xs text-faint" aria-live="polite">
          {remainingSec > 0 ? `${remainingSec}s` : 'Klar'}
        </p>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          autoFocus
          onClick={onContinue}
          className="flex-1 rounded-xl bg-indigo-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30"
        >
          Jeg er klar
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="flex-1 btn-secondary py-3 font-semibold"
        >
          Spring over
        </button>
      </div>
      <p className="text-center text-xs text-faint">
        Enter: start runden · Du kan slå dette helt fra i indstillinger
      </p>
    </div>
  );
}
